import { FC, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useFormContext, Controller } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { CUSTOMER_NAME_QUERY } from '@app/shop/pages/Checkout/Address/AddressForm/AddressForm.queries';
import TextField from '@app/components/input/TextField';

import messages from '../Address.messages';
import { AddressFormFields } from '../Address.constants';
import ValidationMessage from '../ValidationMessage/ValidationMessage';
import { Customer } from './__generated__/Customer';

export const NameFields: FC<{
  namespace: string;
}> = ({ namespace }) => {
  const { errors, getValues, setValue } = useFormContext();
  const { data, loading } = useQuery<Customer>(CUSTOMER_NAME_QUERY);
  const customerFirstName = data?.magento2?.customer?.firstname ?? '';
  const customerLastName = data?.magento2?.customer?.lastname ?? '';

  useEffect(() => {
    const currentFirstName = getValues(
      `${namespace}.${AddressFormFields.FirstName}`
    );

    if (!loading && !currentFirstName) {
      setValue(
        `${namespace}.${AddressFormFields.FirstName}`,
        customerFirstName
      );
    }

    const currentLastName = getValues(
      `${namespace}.${AddressFormFields.LastName}`
    );

    if (!loading && !currentLastName) {
      setValue(`${namespace}.${AddressFormFields.LastName}`, customerLastName);
    }
  }, [
    setValue,
    getValues,
    namespace,
    customerLastName,
    loading,
    customerFirstName,
  ]);

  return (
    <>
      <Controller
        name={`${namespace}.${AddressFormFields.FirstName}`}
        defaultValue=""
        render={({ ref, ...props }) => (
          <TextField
            autoComplete="given-name"
            maxLength={128}
            isValid={!errors[namespace]?.[AddressFormFields.FirstName]}
            formLabel={<FormattedMessage {...messages.firstName} />}
            half
            innerRef={ref}
            {...props}
          />
        )}
        rules={{
          required: {
            value: true,
            //  @ts-expect-error: TS only support string, not ReactElement (react-hook-form.com/api#register)
            message: (
              <ValidationMessage type="required" fieldNameKey="firstName" />
            ),
          },
        }}
      />

      <Controller
        name={`${namespace}.${AddressFormFields.LastName}`}
        defaultValue=""
        render={({ ref, ...props }) => (
          <TextField
            autoComplete="family-name"
            maxLength={128}
            isValid={!errors[namespace]?.[AddressFormFields.LastName]}
            formLabel={<FormattedMessage {...messages.lastName} />}
            innerRef={ref}
            {...props}
          />
        )}
        rules={{
          required: {
            //  @ts-expect-error: TS only support string, not ReactElement (react-hook-form.com/api#register)
            message: (
              <ValidationMessage type="required" fieldNameKey="lastName" />
            ),
            value: true,
          },
        }}
      />
    </>
  );
};
